import { create } from 'zustand'
import {
  api,
  ApiError,
  type StampActivityProgramBlock,
  type StampContext,
  type StampLotteryKeywordResult,
  type StampPrizeSummary,
  type StampProgramBlock,
  type StampQrProgramBlock,
  type StampScanResult,
  type StampTicket,
} from '../services/api'

// スタンプラリー専用ストア (#1126)。useStore は決済/予約/注文の中核で 1,500 行超の
// ため、useStoreOwner の前例に倣い自己完結ドメインとして分離する。ログイン状態は
// 参照しない（サーバが Authorization の有無で匿名/会員を判定する）。

const REGION_KEY = 'yokoso.stampRegion.v1'

/** #2490 合言葉でその場で引いた演出を出しておく時間 (くじの回転 1.3 秒 + 当たりの演出) */
const KEYWORD_REVEAL_MS = 4000

function readManualRegion(): string | null {
  try {
    return sessionStorage.getItem(REGION_KEY)
  } catch {
    return null
  }
}

/**
 * #1898 開催中のイベントに並ぶ企画を平らにする。
 *
 * context はイベント (親) ごとにまとまって返る。スキャン結果の反映やチケット画面は
 * 「どのイベントの企画か」を問わず企画の ID で探すので、ここでまとめて引けるようにする。
 */
export function allPrograms(context: StampContext | null): StampProgramBlock[] {
  if (!context?.available) return []
  return context.events.flatMap((block) => block.programs)
}

/** QR ラリーの企画。eventId を省くと最初の 1 件 (チケット画面の従来の既定)。 */
export function findQrProgram(
  context: StampContext | null,
  eventId?: string | null,
): StampQrProgramBlock | null {
  const qr = allPrograms(context).filter((p): p is StampQrProgramBlock => p.kind === 'qr')
  return (eventId ? qr.find((p) => p.event.id === eventId) : qr[0]) ?? null
}

/** 外部アクティビティ (AR / 外部アプリ) の企画。 */
export function findActivityProgram(
  context: StampContext | null,
  eventId: string,
): StampActivityProgramBlock | null {
  return (
    allPrograms(context).find(
      (p): p is StampActivityProgramBlock => p.kind === 'activity' && p.event.id === eventId,
    ) ?? null
  )
}

/** 企画 1 件だけを差し替えた context を返す (他のイベント・企画はそのまま)。 */
function patchProgram(
  context: StampContext | null,
  eventId: string,
  patch: (program: StampProgramBlock) => StampProgramBlock,
): StampContext | null {
  if (!context?.available) return context
  return {
    ...context,
    events: context.events.map((block) => ({
      ...block,
      programs: block.programs.map((p) => (p.event.id === eventId ? patch(p) : p)),
    })),
  }
}

function withPrize(
  context: StampContext | null,
  eventId: string,
  prize: StampPrizeSummary | null,
): StampContext | null {
  return patchProgram(context, eventId, (p) => ({ ...p, prize }) as StampProgramBlock)
}

interface StampRallyState {
  context: StampContext | null
  contextLoading: boolean
  contextError: boolean
  manualRegionId: string | null
  ticket: StampTicket | null
  ticketLoading: boolean

  /** TOP 表示判定 + 一括データ取得。coords があれば GPS、無ければ手動地域 */
  loadContext: (coords?: { lat: number; lng: number }, eventId?: string) => Promise<void>
  /** 位置情報が使えない時の手動地域選択（sessionStorage 永続） */
  setManualRegion: (regionId: string) => Promise<void>
  /** StampGate のスキャン結果を進捗・獲得済みフラグ・在庫へ即時反映 */
  applyScanResult: (result: StampScanResult) => void
  /** #2490 合言葉を入れた結果を企画に反映する (サーバの応答の値をそのまま使う) */
  applyKeywordResult: (eventId: string, result: StampLotteryKeywordResult) => void
  /** 開催中の QR ラリー全件の進捗を再取得 */
  refreshProgress: () => Promise<void>
  /** 外部アクティビティ (#1154/#1159) 全件の進捗を再取得 (復帰時に呼ぶ) */
  refreshActivityProgress: () => Promise<void>
  /** eventId 省略時は最初の QR 企画 (後方互換)。ARチケットは AR 企画の id を渡す */
  loadTicket: (eventId?: string) => Promise<void>
  claimTicket: (eventId?: string) => Promise<{ ok: boolean; reason?: 'not_eligible' | 'sold_out' | 'error' }>
}

/** 企画の進捗と景品をまとめて取り直す。失敗した企画は前回値のまま。 */
async function refreshPrograms(
  programs: StampProgramBlock[],
  getContext: () => StampContext | null,
  setContext: (context: StampContext | null) => void,
): Promise<void> {
  if (programs.length === 0) return
  const results = await Promise.allSettled(
    programs.map((p) => api.getStampProgress(p.event.id)),
  )
  let next = getContext()
  results.forEach((result, i) => {
    // 進捗の再取得失敗は致命的でない（表示は前回値のまま）
    if (result.status !== 'fulfilled') return
    const { progress, prize, lottery, myDraw, keywordEntered } = result.value
    next = patchProgram(next, programs[i].event.id, (p) => ({
      ...p,
      progress,
      prize,
      // #2490 抽選の結果と合言葉の状態も取り直す。スキャン画面で引いた結果や、別の端末で
      // 入れた合言葉が、スタンプ帳に戻ったときに反映されないと「引けていない」に見える。
      // 古いサーバ (フィールドが無い) では前回値のまま
      ...(lottery !== undefined ? { lottery } : {}),
      ...(myDraw !== undefined ? { myDraw } : {}),
      ...(keywordEntered !== undefined ? { keywordEntered } : {}),
    }) as StampProgramBlock)
  })
  setContext(next)
}

export const useStampRally = create<StampRallyState>((set, get) => ({
  context: null,
  contextLoading: false,
  contextError: false,
  manualRegionId: readManualRegion(),
  ticket: null,
  ticketLoading: false,

  loadContext: async (coords, eventId) => {
    set({ contextLoading: true, contextError: false })
    try {
      const manualRegionId = get().manualRegionId
      const context = await api.getStampContext({
        ...(coords ? { lat: coords.lat, lng: coords.lng } : { regionId: manualRegionId ?? undefined }),
        // #1869 リハーサルは地域から引けないので、名指しがあれば渡す。
        ...(eventId ? { eventId } : {}),
      })
      set({ context, contextLoading: false })
    } catch {
      set({ contextError: true, contextLoading: false })
    }
  },

  setManualRegion: async (regionId) => {
    try {
      sessionStorage.setItem(REGION_KEY, regionId)
    } catch {
      // sessionStorage unavailable — メモリ上の state だけで続行
    }
    set({ manualRegionId: regionId })
    await get().loadContext()
  },

  applyScanResult: (result) => {
    const { context } = get()
    // サーバ確定値の反映のみ（楽観更新はしない — 付与判定は常にサーバ）
    if (!findQrProgram(context, result.event.id)) return
    const stamp = result.stamp
    set({
      context: patchProgram(context, result.event.id, (p) => {
        if (p.kind !== 'qr') return p
        return {
          ...p,
          prize: result.prize ?? p.prize,
          progress: stamp
            ? {
                stampCount: stamp.stampCount,
                goal: stamp.goal,
                achieved: stamp.achieved,
                stampedStoreIds: Array.from(
                  new Set([...(p.progress?.stampedStoreIds ?? []), result.store.id]),
                ),
              }
            : p.progress,
          stores: p.stores.map((s) =>
            s.id === result.store.id && stamp ? { ...s, stamped: true } : s,
          ),
        }
      }),
    })
  },

  applyKeywordResult: (eventId, result) => {
    set({
      context: patchProgram(get().context, eventId, (p) => {
        if (p.kind !== 'qr') return p
        return {
          ...p,
          keywordEntered: true,
          lottery: result.summary ?? p.lottery,
          myDraw: result.lottery ?? p.myDraw,
        }
      }),
    })
    // **くじの演出は 1 回だけ。** `justDrawn` を残したままにすると、企画の枠を閉じて開き直す
    // たびに「抽選中…」が回り直し、引き直しているように見える (justDrawn はそれを防ぐための印)。
    // 演出 (1.3 秒) と当たりの演出が終わるころに落とす
    if (result.lottery?.justDrawn) {
      window.setTimeout(() => {
        set({
          context: patchProgram(get().context, eventId, (p) =>
            p.kind === 'qr' && p.myDraw?.justDrawn
              ? ({ ...p, myDraw: { ...p.myDraw, justDrawn: false } } as StampProgramBlock)
              : p,
          ),
        })
      }, KEYWORD_REVEAL_MS)
    }
  },

  refreshProgress: async () => {
    const qr = allPrograms(get().context).filter((p) => p.kind === 'qr')
    await refreshPrograms(qr, () => get().context, (context) => set({ context }))
  },

  refreshActivityProgress: async () => {
    // 進捗を記録しない外部企画 (linkOnly) は取りに行かない — 返ってくるのは空だけ
    const activities = allPrograms(get().context).filter(
      (p) => p.kind === 'activity' && !p.event.linkOnly,
    )
    await refreshPrograms(activities, () => get().context, (context) => set({ context }))
  },

  loadTicket: async (eventId) => {
    const targetId = eventId ?? findQrProgram(get().context)?.event.id
    if (!targetId) return
    set({ ticketLoading: true })
    try {
      const { ticket, prize } = await api.getMyStampTicket(targetId)
      set({ ticket, ticketLoading: false, context: withPrize(get().context, targetId, prize) })
    } catch {
      set({ ticketLoading: false })
    }
  },

  claimTicket: async (eventId) => {
    const targetId = eventId ?? findQrProgram(get().context)?.event.id
    if (!targetId) return { ok: false, reason: 'error' as const }
    set({ ticketLoading: true })
    try {
      const { ticket, prize } = await api.claimStampTicket(targetId)
      set({ ticket, ticketLoading: false, context: withPrize(get().context, targetId, prize) })
      return { ok: true }
    } catch (e) {
      set({ ticketLoading: false })
      if (e instanceof ApiError) {
        if (e.status === 400) return { ok: false, reason: 'not_eligible' as const }
        if (e.status === 409) return { ok: false, reason: 'sold_out' as const }
      }
      return { ok: false, reason: 'error' as const }
    }
  },
}))
